<template>
  <GuDropdownMenu>
    <GuDropdownMenuTrigger hide-arrow as-child>
      <GuButton
        :id="`ag-grid-cell-renderer-User-Actions-${params.data?.['id'] || ''}`"
        variant="link"
        class="user-actions-context-menu-button"
        :aria-label="`View actions for application ID ${params.data?.['id'] || ''}`"
        role="button"
      >
        ...
      </GuButton>
    </GuDropdownMenuTrigger>

    <GuDropdownMenuContent>
      <GuDropdownMenuItem
        v-for="action in actions"
        :key="action.name"
        :class="action.cssClasses"
        :disabled="action.disabled"
        :aria-disabled="action.disabled"
        @click="action.action"
      >
        {{ action.name }}
      </GuDropdownMenuItem>
    </GuDropdownMenuContent>
  </GuDropdownMenu>

  <Teleport to="body">
    <GuDialog v-model:open="displayConfirmation">
      <GuDialogContent>
        <template #title>
          <h5 class="modal-title fs-18">Confirmation</h5>
        </template>

        <p>
          There are
          {{ selectedRow?.data?.pausedOrInProgressInstanceCount || 0 }}
          active deadline instances with this definition. This action will
          cancel all active instances.
          <br>
          Are you sure you want to delete the definition
          <strong>{{ selectedRow?.data?.name || '' }}</strong>?
        </p>

        <template #footer>
          <GuDialogClose as-child>
            <GuButton variant="secondary">Cancel</GuButton>
          </GuDialogClose>

          <GuButton
            :loading="isDeleting"
            variant="danger"
            @click="deleteDeadline"
          >
            Delete
          </GuButton>
        </template>
      </GuDialogContent>
    </GuDialog>
  </Teleport>
</template>

<script setup>
import { computed, ref, toRefs } from 'vue'
import {
  deleteDeadlineDefinition,
  disableDeadlineDefinition,
  enableDeadlineDefinition,
} from '@deadlines/api/deadlines.api'

import { GuButton } from '@shared/gu-components/button'
import {
  GuDialog,
  GuDialogClose,
  GuDialogContent,
} from '@shared/gu-components/dialog'
import {
  GuDropdownMenu,
  GuDropdownMenuContent,
  GuDropdownMenuItem,
  GuDropdownMenuTrigger,
} from '@shared/gu-components/dropdown-menu'
import { useToast } from '@shared/gu-components/toast'

const props = defineProps({
  params: {
    type: Object,
    default: () => ({}),
  },
})
const { params } = toRefs(props)
const { toast } = useToast()

const displayConfirmation = ref(false)
const isDeleting = ref(false)
const selectedRow = ref(null)

const actions = computed(() => {
  const contextMenuItems = getContextMenuItems({
    node: params.value.node,
    column: params.value.column,
    value: params.value.value,
    api: params.value.api,
    columnApi: params.value.columnApi,
    context: params.value.context,
  })

  return contextMenuItems
})

const getContextMenuItems = (contextMenuParams) => {
  const customItems = defineActions(contextMenuParams.node).map((action) => {
    return {
      name: action.name,
      action: () => action.action(contextMenuParams.node),
    }
  })

  return customItems
}

// Custom function to define actions
const defineActions = (rowData) => {
  const actionsList = [
    {
      name: 'Edit Deadline Definition',
      action: (row) =>
        window.location.assign(
          `/deadline/edit-deadline-definition/${row.data.id}`,
        ),
    },
    {
      name: 'Delete Deadline Definition',
      action: (row) => openConfirmation(row),
    },
  ]

  const DISABLE_DEADLINE = {
    name: 'Disable Deadline Definition',
    action: (row) => disableDeadline(row),
  }

  const ENABLE_DEADLINE = {
    name: 'Enable Deadline Definition',
    action: (row) => enableDeadline(row),
  }

  if (rowData.data.active) actionsList.splice(1, 0, DISABLE_DEADLINE)
  else actionsList.splice(1, 0, ENABLE_DEADLINE)

  return actionsList
}

// Enable a deadline definition
const enableDeadline = async (row) => {
  const rowNode = params.value.api.getRowNode(row.id)

  const result = await enableDeadlineDefinition(row.data.id)

  if (result.success) {
    rowNode.setDataValue('active', true)
    toast({
      description: `${row.data.name} was successfully enabled`,
      title: 'Enable succeeded',
      variant: 'info',
    })
  } else {
    toast({
      description: result.message,
      title: 'Failed',
      variant: 'warning',
    })
  }
}

// Disable a deadline definition
const disableDeadline = async (row) => {
  const rowNode = params.value.api.getRowNode(row.id)

  const result = await disableDeadlineDefinition(row.data.id)

  if (result.success) {
    rowNode.setDataValue('active', false)
    toast({
      description: `${row.data.name} was successfully disabled`,
      title: 'Disable succeeded',
      variant: 'info',
    })
  } else {
    toast({
      description: result.message,
      title: 'Failed',
      variant: 'warning',
    })
  }
}

// Delete a deadline definition
const deleteDeadline = async () => {
  isDeleting.value = true

  const result = await deleteDeadlineDefinition(selectedRow.value.data.id)

  if (result.success) {
    isDeleting.value = false
    closeConfirmation()
    toast({
      description: `${selectedRow.value.data.name} was successfully deleted`,
      title: 'Delete succeeded',
      variant: 'info',
    })
    setTimeout(() => params.value.api.refreshServerSideStore({ purge: true }), 100)
  } else {
    isDeleting.value = false
    closeConfirmation()
    toast({
      description: result.message,
      title: 'Failed',
      variant: 'warning',
    })
  }
}

// Open confirmation dialog
const openConfirmation = (row) => {
  selectedRow.value = row
  displayConfirmation.value = true
}

// Close confirmation dialog
const closeConfirmation = () => {
  displayConfirmation.value = false
}
</script>
